<template>
  <div class="gmb-attributes-preview">
    <div class="attribute-group" v-for="group in groups" :key="group.groupName">
      <div class="group-name">{{ group.groupName }}</div>
      <div
        class="group-items"
        v-for="attribute in group.items"
        :key="attribute.name"
      >
        <div class="attribute-subgroup" v-if="attribute.values">
          <div class="subgroup-name">{{ attribute.name }}</div>
          <div
            class="attribute"
            v-for="item in attribute.values"
            :key="item.name"
            :class="{ active: item.value && !deleteMode }"
          >
            <v-icon v-if="!deleteMode">{{ getIcon(item.value) }}</v-icon>
            {{ item.name }}
          </div>
        </div>
        <div
          class="attribute"
          :class="{ active: attribute.value && !deleteMode }"
          v-else
        >
          <v-icon v-if="!deleteMode">{{ getIcon(attribute.value) }}</v-icon>
          {{ attribute.name }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { GmbAttributeValueType } from "@/enums/gmb-attribute-value-type";
import { sortBy } from "lodash";

export default {
  name: "GmbAttributesPreview",
  props: {
    facilityAttributes: {
      type: Array,
      required: true
    },
    deleteMode: {
      type: Boolean,
      default: () => false
    }
  },
  data() {
    return {
      groups: []
    };
  },
  watch: {
    facilityAttributes() {
      this.mapFacilityAttributes();
    }
  },
  created() {
    this.mapFacilityAttributes();
  },
  methods: {
    mapFacilityAttributes() {
      this.groups = sortBy(this.facilityAttributes, "gmb_attribute.id").reduce(
        (result, { gmb_attribute, values }) => {
          const groupIndex = result.findIndex(
            ({ groupName }) => groupName === gmb_attribute.group_display_name
          );
          if (groupIndex > -1) {
            result[groupIndex].items.push(
              this.mapAttributeValues(gmb_attribute, values)
            );
          } else {
            result.push({
              groupName: gmb_attribute.group_display_name,
              items: [this.mapAttributeValues(gmb_attribute, values)]
            });
          }

          return result;
        },
        []
      );
    },
    mapAttributeValues(attribute, values) {
      if (attribute.value_type === GmbAttributeValueType.BOOL) {
        return this.getBoolValue(
          attribute.value_metadata
            ? attribute.value_metadata[0].displayName
            : attribute.display_name,
          values[0]
        );
      }
      if (attribute.value_type === GmbAttributeValueType.ENUM) {
        const getValues = !this.deleteMode
          ? this.getEnumValues
          : this.getRepeatedEnumValues;
        return getValues(
          attribute.display_name,
          attribute.value_metadata,
          values
        );
      }
      if (attribute.value_type === GmbAttributeValueType.REPEATED_ENUM) {
        return this.getRepeatedEnumValues(
          attribute.display_name,
          attribute.value_metadata,
          values
        );
      }

      return null;
    },
    getBoolValue(name, value) {
      return {
        name,
        value
      };
    },
    getEnumValues(name, metadata, values) {
      const activeMetadata = metadata.filter(item =>
        values.includes(item.value)
      );

      return {
        name,
        values: activeMetadata.map(({ displayName }) => ({
          name: displayName,
          value: true
        }))
      };
    },
    getRepeatedEnumValues(name, metadata, values) {
      return {
        name,
        values: values.map(({ name, value }) => ({
          name: metadata.find(meta => meta.value === name).displayName,
          value
        }))
      };
    },
    getIcon(value) {
      return value ? "icon-check" : "mdi-cancel";
    }
  }
};
</script>

<style lang="scss" scoped>
.attribute-group {
  &:not(:first-child) {
    padding-top: $spacing * 2;
  }
}

.group-name {
  @include font-size(14px);
  font-weight: 500;
  line-height: 20px;
  color: $grey-darker;
  margin-bottom: $spacing * 1.5;
  @include borderAccent($primary, 17px);
}

.attribute-subgroup {
  margin-top: $spacing;
  margin-bottom: $spacing;
  padding: $spacing * 1.5 $spacing $spacing * 0.25;
  border-radius: 12px;
  background: $attribute-group-bg-color;

  .attribute {
    margin-left: 8px;
  }
}

.subgroup-name {
  @include font-size(14px);
  line-height: 17px;
  font-weight: 500;
  color: $grey-darker;
  margin-left: $spacing;
}

.attribute {
  display: flex;
  align-items: center;
  font-weight: normal;
  @include font-size(14px);
  line-height: 17px;
  letter-spacing: -0.01em;
  color: $attribute-inactive-color;
  margin-top: $spacing;
  margin-bottom: $spacing;

  .v-icon {
    @include font-size(20px);
    margin-right: $spacing;
    color: $attribute-inactive-color;
  }

  &.active .v-icon {
    color: $attribute-active-color;
  }
}
</style>
